import { BASE_PATH } from './config'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { postDailyResultsToServer } from './controllers/stats'
import { Router } from 'wouter'
import { syncArchiveResults } from './controllers/archive'
import { useWindowSize } from 'react-use'
import ArchivePage from './components/ArchivePage'
import BottomBanner from './components/BottomBanner'
import Game from './components/Game'
import Header from './components/Header'
import Menu from './components/Menu'
import ModalHelp from './components/ModalHelp'
import ModalSettings from './components/ModalSettings'
import ModalStats from './components/ModalStats'
import React, { FC, useEffect } from 'react'
import scrollLockOptions from './util/scrollLockOptions'
import usePageRouter, {
	isArchiveListPage,
	isArchivePuzzlePage,
	isDailyPuzzlePage,
	isPuzzlePage,
} from './hooks/usePageRouter'

const App: FC = () => {
	const [modal, setModal] = React.useState('')
	const [menuOpen, setMenuOpen] = React.useState(false)
	const { width } = useWindowSize()
	const page = usePageRouter()

	// App Initalisation - happens on initial page load
	// ========================================================================
	useEffect(() => {
		// Check if there any results to sync to the server
		postDailyResultsToServer()
		syncArchiveResults()
	}, [])

	// Set classes on the body when modal/menu is open
	useEffect(() => {
		document.body.classList.toggle('body--modal-active', modal !== '')
		document.body.classList.toggle('body--menu-active', menuOpen)
	}, [modal, menuOpen])

	const openModal = (modalName: string) => {
		setMenuOpen(false)
		setModal(modalName)
		clearAllBodyScrollLocks()
	}

	const closeModal = () => {
		setModal('')
		const isGameComplete =
			document.querySelector('.game--complete0') ||
			document.querySelector('.game--complete1') ||
			document.querySelector('.game--complete2')
		if (!isGameComplete) {
			disableBodyScroll(document.querySelector('.board'), scrollLockOptions)
		}
	}

	const openMenu = () => setMenuOpen(true)
	const closeMenu = () => setMenuOpen(false)

	// Close the modal/menu when the overlay (beneath menu/modal) is clicked
	const onClickOverlay = () => {
		closeModal()
		closeMenu()
	}

	let className = 'app'

	// If we are displayed the desktop banner ad, we need to apply styling to the app
	// to allow for 90px of space at the bottom of the app
	const showFooterAd = width >= 728
	if (showFooterAd) className += ' app--desktop'

	let headerSubtitle = ''
	if (isArchiveListPage(page)) headerSubtitle = 'Archive'
	if (isArchivePuzzlePage(page)) headerSubtitle = `Archive Search #${page.puzzleNumber}`
	if (isDailyPuzzlePage(page)) headerSubtitle = `Daily Search #${page.puzzleNumber}`

	return (
		<>
			<div className={className}>
				<Header
					subtitle={headerSubtitle}
					onClickMenu={() => openMenu()}
					onClickHelp={() => openModal('help')}
					onClickStats={() => openModal('stats')}
				/>

				{isPuzzlePage(page) && (
					<Game
						openModal={openModal}
						puzzleType={page.puzzleType} // could be derived
						puzzleNumber={page.puzzleNumber} //
						puzzleDate={page.puzzleDate} // could be derived
					/>
				)}

				{isArchiveListPage(page) && <ArchivePage />}

				<div className="app__overlay" onClick={onClickOverlay} />

				<ModalHelp active={modal === 'help'} onClose={closeModal} openModal={openModal} />
				<ModalStats active={modal === 'stats'} onClose={closeModal} />
				<ModalSettings active={modal === 'settings'} onClose={closeModal} />

				<Menu active={menuOpen} openModal={openModal} onClose={closeMenu} />
			</div>

			{isPuzzlePage(page) && <BottomBanner />}
		</>
	)
}

const AppWithRouter = () => (
	// Setting the base path makes sure calls to navigate() take into account
	// the subdirectory path. I.e, /onewordsearch and /onewordsearch_c87654ed
	<Router base={BASE_PATH}>
		<App />
	</Router>
)

export default AppWithRouter
